import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, Zap, Car, Wrench, CheckCircle2, ChevronRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const HERO_IMAGE = 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1400&q=85';
const GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80',
  'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&q=80',
  'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600&q=80',
  'https://images.unsplash.com/photo-1552197892-f2ad2f75e7c8?w=600&q=80',
];

const PARTNER_LOGOS = [
  { name: '1komma5°', url: null },
  { name: 'Verne', url: null },
  { name: 'EDP', url: null },
  { name: 'Holaluz', url: null },
];

const SERVICE_ICONS = [Zap, Car, Wrench];

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const services = [
    t('services.pv', { returnObjects: true }),
    t('services.ev', { returnObjects: true }),
    t('services.repairs', { returnObjects: true }),
  ];
  const serviceTestIds = ['service-card-pv', 'service-card-ev', 'service-card-repairs'];

  const metrics = [
    { ...t('metrics.workers', { returnObjects: true }) },
    { ...t('metrics.teams', { returnObjects: true }) },
    { label: t('metrics.capacity.label'), value: t('metrics.capacity.value'), unit: t('metrics.capacity_unit') },
    { ...t('metrics.experience', { returnObjects: true }) },
  ];

  const principles = t('why.principles', { returnObjects: true }) || [];
  const accordionItems = t('why.accordion', { returnObjects: true }) || [];
  const offerItems = t('partnerships.offer_items', { returnObjects: true }) || [];

  return (
    <>
      <Helmet>
        <title>ENERGIESSE SL — Solar Installation Subcontractor for Europe</title>
        <meta
          name="description"
          content="Spanish solar installation subcontractor for EPCs and energy companies in Germany, Austria, and Switzerland. Two teams, ~2 installs/day, full documentation."
        />
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-[var(--energy-bg)]" data-testid="hero-section">
        <div className="hero-overlay absolute inset-0 pointer-events-none" />
        <div className="container-site relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-16 md:py-24 items-center">
            {/* Left: Content */}
            <motion.div
              className="lg:col-span-7 space-y-6"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.div variants={fadeUp}>
                <Badge
                  variant="secondary"
                  className="bg-[var(--energy-green-soft)] text-[var(--energy-green)] border-0 text-xs font-medium"
                >
                  {t('hero.badge')}
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-semibold tracking-tight text-foreground leading-tight"
              >
                {t('hero.headline')}
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-[54ch]"
              >
                {t('hero.subheadline')}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary h-11"
                  onClick={() => navigate('/contact')}
                  data-testid="hero-cta-primary"
                >
                  {t('hero.cta_primary')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/services')}
                  className="h-11 border-border hover:bg-muted"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.cta_secondary')}
                </Button>
              </motion.div>

              {/* Metrics strip */}
              <motion.div
                variants={fadeUp}
                className="flex flex-wrap gap-6 pt-2"
                data-testid="hero-metrics-strip"
              >
                {[
                  { label: t('metrics.workers.label'), value: '6' },
                  { label: t('metrics.teams.label'), value: '2' },
                  { label: t('metrics.capacity.label'), value: '~2/day' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i > 0 && <div className="w-px h-6 bg-border" />}
                    <div>
                      <span className="stat-number text-xl font-semibold text-foreground">
                        {item.value}
                      </span>
                      <span className="text-xs text-muted-foreground ml-1.5">{item.label}</span>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Hero Image */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-xl">
                <img
                  src={HERO_IMAGE}
                  alt="Solar installation team on rooftop"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white/90 text-xs font-medium">
                    {t('hero.image_caption')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* METRICS SECTION */}
      <section className="py-12 bg-white border-y border-border" data-testid="key-metrics-section">
        <div className="container-site">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {metrics.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.07 }}
                className="text-center md:text-left"
              >
                <div className="stat-number text-3xl font-semibold text-foreground">
                  {item.value}
                  {item.unit && <span className="text-base font-normal text-muted-foreground ml-1">{item.unit}</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{item.label}</div>
                {i < metrics.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/4 bottom-1/4 w-px bg-border" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="section-padding bg-[var(--energy-bg)]" data-testid="home-services-section">
        <div className="container-site">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-10"
          >
            <motion.div variants={fadeUp} className="space-y-2">
              <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                {t('services.title')}
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
                {t('services.subtitle')}
              </h2>
            </motion.div>

            <motion.div
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-3 gap-5"
            >
              {services.map((service, i) => {
                const Icon = SERVICE_ICONS[i];
                return (
                  <motion.div key={i} variants={fadeUp}>
                    <Card
                      className="h-full border-border card-hover"
                      data-testid={serviceTestIds[i]}
                    >
                      <CardContent className="p-6 space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="w-10 h-10 rounded-lg bg-[var(--energy-green-soft)] flex items-center justify-center">
                            <Icon className="h-5 w-5 text-[var(--energy-green)]" />
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(service.badges || []).slice(0, 2).map((badge, bi) => (
                              <Badge
                                key={bi}
                                variant="secondary"
                                className="text-[10px] font-medium bg-muted text-muted-foreground"
                              >
                                {badge}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-base mb-2">
                            {service.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {service.description}
                          </p>
                        </div>
                        <ul className="space-y-1.5">
                          {(service.features || []).map((feat, fi) => (
                            <li key={fi} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <CheckCircle2 className="h-3.5 w-3.5 text-[var(--energy-green)] flex-shrink-0" />
                              {feat}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div variants={fadeUp}>
              <Button
                variant="ghost"
                onClick={() => navigate('/services')}
                className="text-[var(--energy-green)] hover:text-[var(--energy-green)] hover:bg-[var(--energy-green-soft)] gap-1.5"
              >
                {t('services.learn_more')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHY WORK WITH US */}
      <section className="section-padding bg-white" data-testid="why-work-with-us-section">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Principles */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-2">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('why.title')}
                </p>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
                  {t('why.subtitle')}
                </h2>
              </motion.div>

              <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {principles.map((principle, i) => (
                  <motion.div key={i} variants={fadeUp} className="space-y-1.5">
                    <div className="w-5 h-0.5 bg-[var(--energy-green)] rounded-full" />
                    <h4 className="font-heading font-semibold text-sm">{principle.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{principle.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Accordion details */}
            <motion.div
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <Accordion type="single" collapsible className="space-y-2">
                {accordionItems.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="border border-border rounded-lg px-4 data-[state=open]:border-[var(--energy-green)]/30"
                  >
                    <AccordionTrigger className="font-heading font-medium text-sm py-3 hover:no-underline">
                      {item.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-3">
                      {item.content}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARTNER LOGOS */}
      <section className="py-12 bg-[var(--energy-bg)] border-y border-border" data-testid="partner-logos-section">
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            <div className="text-center space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                {t('partners.title')}
              </p>
              <p className="text-sm text-muted-foreground">{t('partners.subtitle')}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {PARTNER_LOGOS.map((partner, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.07 }}
                  className="flex items-center"
                >
                  <span className="font-heading font-semibold text-lg md:text-xl text-muted-foreground/50 hover:text-muted-foreground transition-colors partner-logo">
                    {partner.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="section-padding bg-white" data-testid="gallery-preview-section">
        <div className="container-site">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-8"
          >
            <motion.div variants={fadeUp} className="flex items-end justify-between">
              <div className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('gallery_preview.title')}
                </p>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">
                  {t('gallery_preview.subtitle')}
                </h2>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/projects')}
                className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {t('gallery_preview.view_all')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>

            <motion.div
              variants={stagger}
              className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
              data-testid="projects-gallery-grid"
            >
              {GALLERY_IMAGES.map((img, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="relative rounded-xl overflow-hidden aspect-square cursor-pointer group"
                  onClick={() => navigate('/projects')}
                >
                  <img
                    src={img}
                    alt={`Solar installation project ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} className="md:hidden">
              <Button
                variant="ghost"
                onClick={() => navigate('/projects')}
                className="gap-1.5 text-sm text-muted-foreground"
              >
                {t('gallery_preview.view_all')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="section-padding bg-[hsl(222,22%,10%)]"
        data-testid="home-cta-section"
      >
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="max-w-2xl space-y-6"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white tracking-tight">
              {t('cta_section.title')}
            </h2>
            <p className="text-[hsl(215,12%,65%)] leading-relaxed">
              {t('cta_section.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white h-11 btn-primary"
                onClick={() => navigate('/contact')}
                data-testid="home-final-cta-button"
              >
                {t('cta_section.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="self-center text-sm text-[hsl(215,12%,50%)]">
                {t('cta_section.secondary')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
